"use client";

import React from "react";
import { Phone, Mail, Globe, Facebook, Instagram, Linkedin, Loader, ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const contactMethods = [{
  icon: Phone,
  title: "Telefon",
  detail: "+49 1791505044",
  link: "tel:+491791505044",
  description: "Mo-Fr, 07:00 - 17:00 Uhr",
  newTab: false
}, {
  icon: Mail,
  title: "E-Mail",
  detail: "Info-TMBau@web.de",
  link: "mailto:Info-TMBau@web.de",
  description: "Antwort in der Regel am selben Tag",
  newTab: false
}, {
  icon: Globe,
  title: "Website",
  detail: "tm-bauservices.de",
  link: "https://tm-bauservices.de",
  description: "Besuchen Sie unsere Homepage",
  newTab: true
}, {
  icon: Linkedin,
  title: "LinkedIn",
  detail: "BauPartner",
  link: "https://www.linkedin.com/in/deinprofil",
  description: "Vernetzen Sie sich mit uns",
  newTab: true
}];
const inquiryOptions = ["Tiefbau", "Erdarbeiten", "Außenanlagen", "Pflasterarbeiten", "Sonstige Anfrage"];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="88005" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-orange-600/10 text-orange-600 rounded-full flex items-center justify-center mb-4">
          <Mail className="size-8" />
        </div>
        <h3 className="text-2xl font-bold tracking-tight text-slate-900">Anfrage erhalten!</h3>
        <p className="text-slate-500 max-w-sm font-medium">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-sm border-slate-200">
          Neue Anfrage stellen
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Bitte geben Sie Ihren Namen an."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-bold text-slate-500">Ihr Name</FormLabel>
              <FormControl>
                <Input placeholder="Max Mustermann" className="rounded-sm border-slate-200 focus-visible:ring-orange-600" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Bitte geben Sie Ihre E-Mail-Adresse an.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-bold text-slate-500">E-Mail-Adresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@beispiel.de" className="rounded-sm border-slate-200 focus-visible:ring-orange-600" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-bold text-slate-500">Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+49 123 4567890" className="rounded-sm border-slate-200 focus-visible:ring-orange-600" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-mono">Optional für schnellere Rücksprache</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Bitte wählen Sie ein Gewerk aus."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-bold text-slate-500">Gewünschte Leistung</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full min-w-0 appearance-none rounded-sm border border-slate-200 bg-transparent px-3 py-1 pr-8 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-orange-600 focus-visible:ring-[3px] focus-visible:ring-orange-600/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-slate-900/30 aria-invalid:border-destructive aria-invalid:ring-destructive/20" {...field}>
                    <option value="" disabled>
                      Gewerk auswählen...
                    </option>
                    {inquiryOptions.map((option, i) => <option key={i} value={option} data-index={i}>
                        {option}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-400">
                    <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Bitte beschreiben Sie Ihr Vorhaben."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider font-bold text-slate-500">Details zum Vorhaben</FormLabel>
            <FormControl>
              <Textarea placeholder="Beschreiben Sie kurz Ihr Bauprojekt (Ort, Umfang, Zeitplan)..." className="min-h-[120px] resize-y rounded-sm border-slate-200 focus-visible:ring-orange-600" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-orange-600 hover:bg-slate-900 text-white rounded-sm font-bold shadow-lg transition-all" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Wird übermittelt...
          </> : <>
            Projekt anfragen
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function SchreibenSieUns() {
  return <section id="lassen-sie-uns-ihr-projekt-planen" className="py-20 relative overflow-hidden !bg-[hsl(41,96%,40%)] !text-[hsl(53,98%,77%)] lg:py-32">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-orange-600/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] bg-slate-900/5 rounded-full blur-3xl" />
        
        <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-50/50 transform origin-top-right skew-x-[-45deg] -z-10 hidden lg:block border-l border-slate-100" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          
          <div className="lg:col-span-5 space-y-10">
            <div>
              <h2 className="text-4xl font-bold tracking-tighter mb-6 uppercase !text-[hsl(0,0%,0%)] md:text-5xl">
                Lassen Sie uns Ihr Bauprojekt planen
              </h2>
              <p className="leading-relaxed !text-center !italic !underline !text-xl !font-extrabold !text-[hsl(0,0%,0%)]">Ob Tiefbau, Erdarbeiten oder Außenanlagen – BauPartner ist Ihr regionaler Experte für saubere Ausführung und faire Preise. Senden Sie uns Ihre Anfrage für ein kostenloses Erstgespräch.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {contactMethods.map((method, i) => <Link key={i} to={method.link} newTab={method.newTab} className="group flex items-start gap-4 p-5 rounded-sm border border-slate-200 bg-white/50 hover:bg-white hover:shadow-xl transition-all duration-300" data-index={i}>
                  <div className="flex-shrink-0 size-12 rounded-sm bg-orange-600/10 text-orange-600 flex items-center justify-center group-hover:bg-orange-600 group-hover:text-white transition-colors duration-300">
                    <method.icon className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider">{method.title}</h3>
                    <p className="text-orange-600 font-mono font-semibold mt-0.5">{method.detail}</p>
                    <p className="text-sm text-slate-500 mt-1">{method.description}</p>
                  </div>
                </Link>)}
            </div>
          </div>

          
          <div className="lg:col-span-7 lg:pl-8">
            <Card className="text-card-foreground flex flex-col gap-6 py-6 shadow-2xl border-slate-200 backdrop-blur-xl relative overflow-hidden rounded-sm !text-[hsl(0,0%,0%)]">
              
              
              
              <CardHeader className="pb-6 pt-10 px-8">
                <CardTitle className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Jetzt kostenlos anfragen</CardTitle>
                <CardDescription className="text-base text-slate-500 font-medium">
                  Teilen Sie uns die Eckdaten Ihres Vorhabens mit. Wir melden uns innerhalb von 24 Stunden.
                </CardDescription>
              </CardHeader>
              <CardContent className="px-8 pb-10">
                <FormProvider formKey="project_plan_form_contact" formId="project_plan_form_contact" sectionName="Projektanfrage" defaultValues={{
                name: "",
                email: "",
                phone: "",
                inquiry_type: "",
                message: ""
              }} submitText="Projekt anfragen" successMessage="Ihre Anfrage wurde erfolgreich übermittelt. Wir prüfen die Kapazitäten und melden uns umgehend bei Ihnen." errorMessage="Leider gab es ein technisches Problem. Bitte rufen Sie uns direkt an oder versuchen Sie es in wenigen Minuten erneut." recipientEmail="InfoTMBau@web.de" sendEmail={true} fieldMap={{
                name: "{{name}}",
                email: "{{email}}",
                phone: "{{phone}}",
                inquiry_type: "{{service}}",
                message: "{{message}}"
              }} emailTemplate={{
                subject: "Neue Projektanfrage von Website",
                message: "Name: {{name}}\nE-Mail: {{email}}\nTelefon: {{phone}}\nGewünschte Leistung: {{service}}\nNachricht:\n{{message}}"
              }} replyTo="{{email}}" fromEmail="noreply@tm-bauservice.de" fromName="T&M Hoch & Tiefbau" useSMTP={true}>
                  <ContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}